import React, { useState } from "react";
import {
  LoadingSpinner,
  MeatballMenu,
  MeatballMenuItem,
  PrimaryBlueButton,
  Severity
} from "@velaro/velaro-component-lib";
import useAzureIndexes from "@/hooks/useAzureIndexes";
import useAzureIndexesApi from "@/hooks/useAzureIndexesApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import useToastAlert from "@/hooks/useToastAlert";
import { AzureIndex } from "../types";
import IndexModal from "./IndexModal";
import DeleteIndexModal from "./DeleteIndexModal";
import IconEditPencil from "@/icons/IconEditPencil";
import IconDeactivateUserPerson from "@/icons/IconDeactivateUserPerson";

export default function AINewIndexes() {
  const { azureIndexes, loading, add, update, remove } = useAzureIndexes();
  const { createIndex, editIndex, deleteIndex } = useAzureIndexesApi();
  const { subscription } = useSubscriptions();
  const toastAlert = useToastAlert();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<AzureIndex | null>(null);
  const [deletingIndex, setDeletingIndex] = useState<AzureIndex | null>(null);

  // Handle Create
  const handleCreate = async (index: AzureIndex) => {
    const result = await createIndex({
      name: index.name,
      type: index.type,
      searchSize: index.searchSize,
      semanticSearchEnabled: index.semanticSearchEnabled
    });

    if (result.success && result.data) {
      add(result.data);
      toastAlert.displayToast(Severity.Success, "Index created successfully");
      setShowCreateModal(false);
    } else {
      toastAlert.displayToast(
        Severity.Error,
        result.error || "Failed to create index"
      );
    }
  };

  // Handle Edit
  const handleEdit = async (index: AzureIndex) => {
    const result = await editIndex({
      id: index.id,
      name: index.name,
      type: index.type,
      searchSize: index.searchSize,
      semanticSearchEnabled: index.semanticSearchEnabled
    });

    if (result.success && result.data) {
      update(result.data);
      toastAlert.displayToast(Severity.Success, "Index updated successfully");
      setEditingIndex(null);
    } else {
      toastAlert.displayToast(
        Severity.Error,
        result.error || "Failed to update index"
      );
    }
  };

  // Handle Delete
  const handleDelete = async (id: number) => {
    const result = await deleteIndex(id);

    if (result.success) {
      remove(id);
      toastAlert.displayToast(Severity.Success, "Index deleted successfully");
      setDeletingIndex(null);
    } else {
      toastAlert.displayToast(
        Severity.Error,
        result.error || "Failed to delete index"
      );
    }
  };

  // Meatball menu items
  const getMeatballItems = (index: AzureIndex): MeatballMenuItem[] => [
    {
      icon: IconEditPencil,
      label: "Edit Index",
      onClick: () => setEditingIndex(index)
    },
    {
      icon: IconDeactivateUserPerson,
      label: "Delete Index",
      onClick: () => setDeletingIndex(index)
    }
  ];

  // Check if at subscription limit
  const atLimit =
    subscription?.maxIndexCount !== undefined &&
    azureIndexes.length >= subscription.maxIndexCount;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-xl font-semibold">AI Indexes</h1>
          <p className="text-sm text-slate-500">
            Manage Azure Search indexes for AI training data
          </p>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <span className="text-sm text-slate-500">
            {azureIndexes.length} / {subscription?.maxIndexCount ?? "∞"} indexes
          </span>
          <PrimaryBlueButton
            label="Create Index"
            onClick={() => setShowCreateModal(true)}
            disabled={atLimit}
          />
        </div>
      </div>

      {/* Limit Warning */}
      {atLimit && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-sm text-amber-700">
            You have reached your subscription limit for indexes. Please upgrade
            your plan or delete unused indexes to create new ones.
          </p>
        </div>
      )}

      {/* Empty State */}
      {azureIndexes.length === 0 ? (
        <div className="bg-white border rounded-lg p-8 text-center">
          <div className="text-slate-400 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6M12 9v6"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-700 mb-2">
            No indexes yet
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Create your first Azure Search index to enable AI-powered search over
            your training data.
          </p>
          <PrimaryBlueButton
            label="Create Your First Index"
            onClick={() => setShowCreateModal(true)}
          />
        </div>
      ) : (
        /* Index Table */
        <div className="bg-white border rounded-lg shadow-sm overflow-visible">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-sm font-medium text-slate-600">Name</th>
                <th className="px-4 py-3 text-sm font-medium text-slate-600">Created</th>
                <th className="px-4 py-3 text-sm font-medium text-slate-600">Last Updated</th>
                <th className="px-4 py-3 text-sm font-medium text-slate-600">Created By</th>
                <th className="px-4 py-3 text-sm font-medium text-slate-600 w-12">{/* Actions */}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {azureIndexes.map((index) => (
                <tr key={index.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <button
                      className="font-medium text-sm text-primary hover:underline text-left"
                      onClick={() => setEditingIndex(index)}
                    >
                      {index.name}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600">
                      {index.createdAt ? new Date(index.createdAt).toLocaleDateString() : "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600">
                      {index.updatedAt ? new Date(index.updatedAt).toLocaleDateString() : "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-500 truncate max-w-[120px] block">
                      {index.createdBy || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <MeatballMenu menuItems={getMeatballItems(index)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-sm font-medium text-blue-800 mb-2">
          How indexes work — hybrid BM25 + embeddings
        </h4>
        <ul className="text-sm text-blue-700 space-y-1.5">
          <li>
            • Every document is stored twice: as a <strong>vector embedding</strong> (semantic meaning) and as a <strong>BM25 keyword index</strong> (exact terms). Most AI platforms use only one — Velaro runs both in parallel and combines the scores.
          </li>
          <li>
            • <strong>Embeddings</strong> catch intent and synonyms ("cheap flight" matches "affordable airfare"). <strong>BM25</strong> catches exact product codes, names, and rare terms that embeddings can miss. Together they produce the most accurate retrieval available.
          </li>
          <li>
            • <strong>Semantic re-ranking</strong> is an optional Azure add-on that runs a second neural pass over results. It is <strong>off by default</strong> — the hybrid BM25 + embeddings pipeline already produces strong results and adding re-ranking introduces latency and Azure cost with mixed real-world benefit.
          </li>
          <li>
            • You can always override retrieval results with <strong>Knowledge Overrides</strong> — pin a specific answer to a topic so it always wins regardless of index scores.
          </li>
          <li>
            • <strong>Search Size</strong> controls how many chunks are retrieved per query. Higher = more context for the AI but also more tokens. Recommended: 5–10.
          </li>
        </ul>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <IndexModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreate}
        />
      )}

      {editingIndex && (
        <IndexModal
          index={editingIndex}
          onClose={() => setEditingIndex(null)}
          onSave={handleEdit}
        />
      )}

      {deletingIndex && (
        <DeleteIndexModal
          index={deletingIndex}
          onClose={() => setDeletingIndex(null)}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
